import { useState, useMemo } from "react";
import { getErrorMessage, post, usePoll } from "../lib/api";
import { formatUtcTime, formatIndianTime, formatIndianDate } from "../lib/operatorFormatters";
import PositionChartModal from "./PositionChartModal";
import { ListFilter, XCircle, ShieldCheck, Sparkles, TrendingUp, TrendingDown, Layers, BarChart3 } from "lucide-react";

const fmt = (v) => (typeof v === "number" ? Number(v.toPrecision(6)) : v || "—");

export default function OpenOrdersTable({ selectedViewAccountId = "paper", onOpenFullChart }) {
  const { data: openOrders, retry } = usePoll(
    `/accounts/${encodeURIComponent(selectedViewAccountId)}/open-orders`,
    10000,
    [],
  );

  const [activeChartOrder, setActiveChartOrder] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);
  const [filterType, setFilterType] = useState("all"); // all, stop, tp

  const orders = useMemo(() => {
    const list = Array.isArray(openOrders?.data) ? openOrders.data : (Array.isArray(openOrders) ? openOrders : []);
    if (filterType === "stop") return list.filter(o => ((o.order_type || o.type || o.origType || "").toLowerCase()).includes("stop"));
    if (filterType === "tp") return list.filter(o => ((o.order_type || o.type || o.origType || "").toLowerCase()).includes("profit"));
    return list;
  }, [openOrders, filterType]);

  const rawOrdersCount = Array.isArray(openOrders?.data) ? openOrders.data.length : (Array.isArray(openOrders) ? openOrders.length : 0);

  const handleCancel = async (orderId, symbol) => {
    setBusyId(orderId);
    setMessage(null);
    try {
      await post(
        `/accounts/${encodeURIComponent(selectedViewAccountId)}/open-orders/${encodeURIComponent(orderId)}/cancel?symbol=${encodeURIComponent(symbol)}`,
      );
      setMessage(`Cancelled order ${orderId} for ${symbol}`);
      retry();
    } catch (e) {
      setMessage(getErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl flex flex-col w-full" data-testid="open-orders-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800/70 flex flex-wrap items-center justify-between gap-3 bg-zinc-900/40 rounded-t-xl">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400">
            <Layers size={15} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-100 font-mono">
                Open Orders ({rawOrdersCount})
              </h3>
            </div>
            <p className="text-[10px] text-zinc-500 font-mono">Active working and conditional orders on Binance</p>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1 text-[10px] font-mono">
          {[
            { id: "all", label: `All (${rawOrdersCount})` },
            { id: "stop", label: "Stop Loss" },
            { id: "tp", label: "Take Profit" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilterType(tab.id)}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                filterType === tab.id
                  ? "bg-sky-500/20 text-sky-400 border border-sky-500/40 font-bold"
                  : "bg-zinc-900/60 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 border border-zinc-800/40"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className="px-4 py-1.5 bg-amber-950/40 border-b border-amber-800/50 text-[11px] font-mono text-amber-300 flex items-center justify-between">
          <span>{message}</span>
          <button onClick={() => setMessage(null)} className="text-amber-500 hover:text-amber-300">✕</button>
        </div>
      )}

      {/* Main Table Container */}
      <div className="infinite-scroll-container flex-1 overflow-x-auto overflow-y-auto max-h-[350px]">
        <table className="w-full text-xs font-mono border-collapse min-w-[750px]">
          <thead className="sticky top-0 z-20 bg-zinc-950/98 backdrop-blur-md text-zinc-400 border-b border-zinc-800 shadow-md">
            <tr className="text-left text-[10px] uppercase tracking-wider">
              <th scope="col" className="py-2.5 px-3 font-semibold text-zinc-400 text-left bg-zinc-950">Symbol</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-left bg-zinc-950">Order Type</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-center bg-zinc-950">Side</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Quantity</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-sky-400/90 text-right bg-zinc-950">Trigger / Price</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-center bg-zinc-950">Time</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-center bg-zinc-950">Status</th>
              <th scope="col" className="py-2.5 px-3 font-semibold text-zinc-400 text-center bg-zinc-950">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900/80">
            {orders.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-zinc-600">
                  <div className="flex flex-col items-center gap-1.5">
                    <ShieldCheck size={22} className="text-zinc-700" />
                    <span>No active working orders on exchange</span>
                  </div>
                </td>
              </tr>
            )}

            {orders.map((o) => {
              const symbolClean = (o.symbol || "").replace(":USDT", "").replace("/USDT", "");
              const side = (o.side || "").toLowerCase();
              const isBuy = side === "buy";
              const typeLabel = (o.order_type || o.type || o.origType || "LIMIT").replace(/_/g, " ").toUpperCase();
              const triggerPrice = o.stop_price || o.triggerPrice || o.price || 0;
              const isStop = typeLabel.includes("STOP");
              const createdAt = o.created_at || o.time || o.updateTime || "";

              return (
                <tr
                  key={o.id}
                  onClick={() => setActiveChartOrder(o)}
                  className="hover:bg-zinc-900/80 transition-colors align-middle cursor-pointer group"
                  title="Click to view live chart & order trigger levels"
                >
                  <td className="py-2.5 px-3 font-bold text-zinc-100 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span>{symbolClean}</span>
                      <span className="text-[9px] text-zinc-500 bg-zinc-900 px-1 py-0.2 rounded border border-zinc-800">USDT</span>
                      <BarChart3 size={12} className="text-zinc-600 group-hover:text-sky-400 transition-colors ml-0.5" />
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-left whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      isStop ? "bg-amber-950/60 text-amber-400 border border-amber-800/40" : "bg-sky-950/60 text-sky-400 border border-sky-800/40"
                    }`}>
                      {typeLabel}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-center whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      isBuy ? "bg-emerald-950/80 text-emerald-400 border border-emerald-800/60" : "bg-rose-950/80 text-rose-400 border border-rose-800/60"
                    }`}>
                      {isBuy ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      {side}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-right text-zinc-300 font-mono whitespace-nowrap">{fmt(o.qty)}</td>
                  <td className="py-2.5 px-2 text-right text-sky-400 font-semibold font-mono whitespace-nowrap">{fmt(triggerPrice)}</td>
                  <td className="py-2.5 px-2 text-center whitespace-nowrap text-[10px]" title={`Exchange UTC: ${formatUtcTime(createdAt)}`}>
                    <div className="font-semibold text-amber-300/90">{formatIndianTime(createdAt)}</div>
                    <div className="text-[9px] text-zinc-500">{formatIndianDate(createdAt, { includeYear: false })}</div>
                  </td>
                  <td className="py-2.5 px-2 text-center whitespace-nowrap">
                    <span className="px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-400 text-[10px] border border-zinc-800">
                      {o.status || "NEW"}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    <button
                      disabled={busyId === o.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancel(o.id, o.symbol);
                      }}
                      className="px-2.5 py-1 rounded border border-rose-900/60 text-rose-400 hover:bg-rose-950 disabled:opacity-50 transition-colors uppercase text-[10px] font-bold"
                    >
                      {busyId === o.id ? "Cancelling..." : "Cancel"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {activeChartOrder && (
        <PositionChartModal
          item={activeChartOrder}
          isOpen={Boolean(activeChartOrder)}
          onClose={() => setActiveChartOrder(null)}
          onOpenFullChart={onOpenFullChart}
        />
      )}
    </div>
  );
}
